import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Upload, AlertTriangle, CheckCircle2, UserPlus, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const monthOptions = (() => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const label = d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    out.push({ iso, label });
  }
  return out;
})();

export default function AttendanceUpload() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [month, setMonth] = useState(monthOptions[0].iso);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Inline "Map to Employee" flow — no need to leave this page anymore
  const [assignTarget, setAssignTarget] = useState(null); // {punch_id, raw_name}
  const [assignedPunchIds, setAssignedPunchIds] = useState(new Set());
  const [empQuery, setEmpQuery] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [assignBusy, setAssignBusy] = useState(false);

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data));
  }, []);

  // Employee autocomplete inside the map dialog
  useEffect(() => {
    if (!assignTarget) return;
    if (empQuery.trim().length < 2) {
      setEmpResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/employees", { params: { search: empQuery } });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        // Backend has no server-side limit param — enforce a small cap client-side
        // to keep the dropdown snappy on large orgs (20k+ employees).
        const q = empQuery.toLowerCase();
        const filtered = arr.filter((e) =>
          (e.name || "").toLowerCase().includes(q) ||
          (e.employee_code || "").toLowerCase().includes(q)
        ).slice(0, 20);
        setEmpResults(filtered);
      } catch {
        setEmpResults([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [empQuery, assignTarget]);

  const doAssign = async (emp) => {
    if (!assignTarget) return;
    setAssignBusy(true);
    try {
      const r = await api.post("/biometric/assign", {
        punch_id: assignTarget.punch_id,
        employee_id: emp.id,
      });
      toast.success(
        `Mapped ${assignTarget.punch_id} → ${emp.employee_code} ${emp.name}. ` +
        `Retro-updated ${r.data?.retro_punches_updated ?? 0} historic punch(es).`
      );
      setAssignedPunchIds((prev) => new Set([...prev, assignTarget.punch_id]));
      setAssignTarget(null);
      setEmpQuery("");
      setEmpResults([]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Map failed");
    } finally {
      setAssignBusy(false);
    }
  };

  const onUpload = async () => {
    if (!unitId || !month || !file) {
      toast.error("Pick unit, month & file");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("unit_id", unitId);
      fd.append("month", month);
      fd.append("file", file);
      const { data } = await api.post("/attendance/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Parsed ${data.matched} matched, ${data.unmatched.length} unmatched`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="attendance-upload-page" className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Link to="/attendance" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Attendance</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            New monthly upload
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload the biometric machine export. Supported: .xls, .xlsx, .csv (auto-detected).
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Unit</Label>
            <Select value={unitId || undefined} onValueChange={setUnitId}>
              <SelectTrigger data-testid="upload-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
              <SelectContent>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="upload-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => <SelectItem key={m.iso} value={m.iso}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center">
          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" />
          <input
            data-testid="upload-file"
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
            className="block mx-auto text-sm"
          />
          {file && (
            <div className="mt-3 text-xs text-slate-600">
              Selected: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)
            </div>
          )}
          <Button
            data-testid="upload-btn"
            onClick={onUpload}
            disabled={!unitId || !month || !file || busy}
            className="mt-4 brand-bg brand-bg-hover text-white"
          >
            {busy ? "Parsing..." : "Parse & ingest"}
          </Button>
          <p className="text-xs text-slate-400 mt-3">
            Re-uploading the same unit + month replaces the previous draft.
          </p>
        </div>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="upload-result">
          {/* Rev. 2026-08 — Parse summary card BEFORE Save/Review buttons */}
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-4 border-b border-slate-100"
            data-testid="upload-parse-summary"
          >
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                Employees Parsed
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Chivo" }}>
                {result.matched + result.unmatched.length}
              </div>
              <div className="text-[11px] text-slate-500">rows in CSV</div>
            </div>
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-green-700 font-medium flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Matched to master
              </div>
              <div className="text-2xl font-bold text-green-900 mt-0.5" style={{ fontFamily: "Chivo" }}>
                {result.matched}
              </div>
              <div className="text-[11px] text-green-700">
                {result.matched + result.unmatched.length > 0
                  ? Math.round((result.matched / (result.matched + result.unmatched.length)) * 100)
                  : 0}
                % coverage
              </div>
            </div>
            <div
              className={`rounded-md border px-3 py-2 ${
                result.unmatched.length > 0
                  ? "border-red-300 bg-red-50"
                  : "border-slate-200 bg-slate-50"
              }`}
            >
              <div
                className={`text-[10px] uppercase tracking-wider font-medium flex items-center gap-1 ${
                  result.unmatched.length > 0 ? "text-red-700" : "text-slate-500"
                }`}
              >
                {result.unmatched.length > 0 && <AlertTriangle className="h-3 w-3" />}
                Unmatched
              </div>
              <div
                className={`text-2xl font-bold mt-0.5 ${
                  result.unmatched.length > 0 ? "text-red-900" : "text-slate-900"
                }`}
                style={{ fontFamily: "Chivo" }}
              >
                {result.unmatched.length}
              </div>
              <div
                className={`text-[11px] ${
                  result.unmatched.length > 0 ? "text-red-700" : "text-slate-500"
                }`}
              >
                {result.unmatched.length > 0
                  ? "not in Employees master — see below"
                  : "all IDs matched ✓"}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span><span className="font-semibold">{result.matched}</span> employees matched · <span className="font-semibold">{result.daily_count}</span> daily records</span>
            </div>
            <div className="flex items-center gap-2">
              {result.anomaly_blocking > 0 && (
                <Button
                  data-testid="goto-anomalies-btn"
                  onClick={() => navigate(`/attendance/anomalies/${result.upload_id}`)}
                  variant="destructive"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Resolve {result.anomaly_blocking} anomal{result.anomaly_blocking === 1 ? "y" : "ies"}
                </Button>
              )}
              <Button
                data-testid="goto-review-btn"
                onClick={() => navigate(`/attendance/review/${result.upload_id}`)}
                className="brand-bg brand-bg-hover text-white"
              >
                Open review →
              </Button>
            </div>
          </div>
          {(result.anomaly_blocking > 0 || result.anomaly_warning > 0) && (
            <div className="border border-red-200 bg-red-50 rounded-md p-4">
              <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                <AlertTriangle className="h-4 w-4" />
                {result.anomaly_blocking + result.anomaly_warning} punch anomal{(result.anomaly_blocking + result.anomaly_warning) === 1 ? "y" : "ies"} detected
              </div>
              <p className="text-xs text-red-800">
                <span className="font-semibold">{result.anomaly_blocking} blocking</span> (must be resolved before finalize)
                {result.anomaly_warning > 0 && <> · <span className="font-semibold">{result.anomaly_warning} warning</span></>}
                . Click &quot;Resolve&quot; above to review.
              </p>
            </div>
          )}
          {result.unmatched.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-4" data-testid="upload-unmatched-panel">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-amber-800 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {result.unmatched.length} unmatched Punch ID{result.unmatched.length === 1 ? "" : "s"}
                  {assignedPunchIds.size > 0 && (
                    <span className="text-emerald-700 text-xs font-normal">
                      · {assignedPunchIds.size} mapped ✓
                    </span>
                  )}
                </div>
                <Link
                  to="/admin/biometric-matching"
                  className="text-xs font-medium text-amber-700 hover:text-amber-900 flex items-center gap-1"
                  data-testid="upload-open-matching-link"
                >
                  Advanced view <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              <p className="text-xs text-amber-800 mb-3">
                Click <b>Map</b> on any row to assign a Punch ID to an existing employee.
                Historic punches with the same ID will be automatically re-linked. No need to leave this page.
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {result.unmatched.map((u) => {
                  const done = assignedPunchIds.has(u.punch_id);
                  return (
                    <div
                      key={u.punch_id}
                      className={`text-xs flex items-center gap-2 bg-white border rounded px-2 py-1.5 ${
                        done ? "border-emerald-300 opacity-70" : "border-amber-200"
                      }`}
                      data-testid={`upload-unmatched-row-${u.punch_id}`}
                    >
                      <span className="font-mono text-[11px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 flex-shrink-0">
                        {u.punch_id}
                      </span>
                      <span className="text-slate-700 truncate flex-1" title={u.raw_name}>
                        {u.raw_name || <em className="text-slate-400">no name in file</em>}
                      </span>
                      {done ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium flex-shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Mapped
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                          onClick={() => {
                            setAssignTarget(u);
                            setEmpQuery("");
                            setEmpResults([]);
                          }}
                          data-testid={`upload-unmatched-map-btn-${u.punch_id}`}
                        >
                          <UserPlus className="h-3 w-3 mr-1" /> Map
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map-to-Employee dialog */}
      <Dialog
        open={!!assignTarget}
        onOpenChange={(o) => {
          if (!o) {
            setAssignTarget(null);
            setEmpQuery("");
            setEmpResults([]);
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="upload-map-dialog">
          <DialogHeader>
            <DialogTitle>Map Punch ID to an Employee</DialogTitle>
            <DialogDescription>
              Punch ID:{" "}
              <span className="font-mono bg-slate-100 rounded px-1.5 py-0.5">
                {assignTarget?.punch_id}
              </span>
              {assignTarget?.raw_name && (
                <> — <span className="text-slate-700">{assignTarget.raw_name}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                autoFocus
                value={empQuery}
                onChange={(e) => setEmpQuery(e.target.value)}
                placeholder="Search employee by name or code (min 2 chars)…"
                className="pl-9"
                data-testid="upload-map-emp-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1 border rounded-md">
              {empQuery.trim().length < 2 ? (
                <div className="text-center text-xs text-slate-400 py-6">
                  Type at least 2 characters to search…
                </div>
              ) : empResults.length === 0 ? (
                <div className="text-center text-xs text-slate-400 py-6">
                  No employee matches &quot;{empQuery}&quot;
                </div>
              ) : (
                empResults.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => doAssign(e)}
                    disabled={assignBusy}
                    data-testid={`upload-map-emp-${e.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 flex items-center justify-between gap-2 disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {e.name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {e.employee_code}
                        {e.biometric_id && (
                          <span className="ml-2 text-amber-700">
                            ⚠ current biometric: {e.biometric_id}
                          </span>
                        )}
                      </div>
                    </div>
                    <UserPlus className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
            <p className="text-[11px] text-slate-500">
              Selecting an employee will set their biometric_id to{" "}
              <span className="font-mono">{assignTarget?.punch_id}</span> and re-link
              all historic punches with this ID.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignTarget(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
